import { z } from 'zod';
import {
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  situacaoAprovacaoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Autorização dos documentos de compra — lista de compra direta, solicitação e
 * pedido (`.spec/015`, decisão 108).
 *
 * **Parâmetro da conta, um por documento**, desligado por padrão
 * (`configuracao_conta.aprovar_*`). Desligado, a situação de aprovação é
 * ignorada e o documento anda como sempre andou. Ligado, só o **aprovado**
 * passa adiante:
 *
 * - a lista não gera pedido;
 * - a solicitação não é origem de pedido — a cotação continua livre, por
 *   decisão do Adriano: cotar antes de aprovar é permitido;
 * - o pedido não sai em PDF nem recebe nota de compra.
 *
 * **Quem decide é a configuração de agora, e não a do dia em que o documento
 * nasceu.** Ligar o parâmetro trava também o que já estava pendente: é o que o
 * administrador espera ao ligar ("daqui para a frente, nada sai sem
 * aprovação"), e é o que permite desligar sem deixar documento preso numa fila
 * que ninguém mais olha.
 *
 * Os valores gravados são os que a coluna da solicitação já tinha desde a fase 1
 * (`NAO_REQUER`, `AGUARDANDO`, `APROVADA`, `REJEITADA`). Os rótulos, não: com o
 * parâmetro, `NAO_REQUER` passou a significar "ainda não enviada", e
 * `REJEITADA` se lê "Reprovada", a palavra de quem pediu o fluxo. Renomear os
 * valores exigiria reescrever as linhas existentes dentro da migração, com o
 * `FORCE ROW LEVEL SECURITY` no caminho — e o nome do valor não aparece em tela
 * nenhuma.
 */

export type SituacaoAprovacao = z.infer<typeof situacaoAprovacaoSchema>;

export const ROTULO_SITUACAO_APROVACAO: Readonly<Record<SituacaoAprovacao, string>> = {
  NAO_REQUER: 'Não enviada',
  AGUARDANDO: 'Aguardando aprovação',
  APROVADA: 'Aprovada',
  REJEITADA: 'Reprovada',
};

/** Os três documentos que o parâmetro alcança. */
export const DOCUMENTOS_APROVACAO = ['LISTA_COMPRA_DIRETA', 'SOLICITACAO', 'PEDIDO'] as const;
export type DocumentoAprovacao = (typeof DOCUMENTOS_APROVACAO)[number];
export const documentoAprovacaoSchema = z.enum(DOCUMENTOS_APROVACAO);

export const ROTULO_DOCUMENTO_APROVACAO: Readonly<Record<DocumentoAprovacao, string>> = {
  LISTA_COMPRA_DIRETA: 'Lista de compra direta',
  SOLICITACAO: 'Solicitação de compra',
  PEDIDO: 'Pedido de compra',
};

/**
 * O que fica gravado no histórico, um evento por gesto.
 *
 * A **reabertura** é um evento, e não o apagamento dos anteriores: o ciclo
 * "enviou, reprovou, reabriu, enviou, aprovou" é o que se quer ler depois, e é o
 * que o Adriano pediu que ficasse registrado.
 */
export const ACOES_APROVACAO = ['ENVIO', 'APROVACAO', 'REPROVACAO', 'REABERTURA'] as const;
export const acaoAprovacaoSchema = z.enum(ACOES_APROVACAO);
export type AcaoAprovacao = z.infer<typeof acaoAprovacaoSchema>;

export const ROTULO_ACAO_APROVACAO: Readonly<Record<AcaoAprovacao, string>> = {
  ENVIO: 'Enviado para aprovação',
  APROVACAO: 'Aprovado',
  REPROVACAO: 'Reprovado',
  REABERTURA: 'Reaberto para alteração',
};

/**
 * A decisão do autorizador. O motivo é obrigatório só na reprovação: é ele que
 * diz a quem lançou o que corrigir antes de enviar de novo, e "reprovado" sem
 * motivo devolveria o documento sem dizer para quê.
 */
export const decisaoAprovacaoSchema = z.discriminatedUnion('decisao', [
  z.object({ decisao: z.literal('APROVAR') }).strict(),
  z
    .object({
      decisao: z.literal('REPROVAR'),
      motivo: z
        .string()
        .trim()
        .min(3, 'Diga o motivo da reprovação — é ele que mostra o que corrigir.')
        .max(1000, 'O motivo tem no máximo 1000 caracteres.'),
    })
    .strict(),
]);
export type DecisaoAprovacao = z.infer<typeof decisaoAprovacaoSchema>;

export const eventoAprovacaoSchema = z.object({
  id: uuidSchema,
  acao: acaoAprovacaoSchema,
  /** Só na reprovação. */
  motivo: z.string().nullable(),
  usuarioId: uuidSchema.nullable(),
  /** Nulo se o usuário foi apagado depois. */
  usuarioNome: z.string().nullable(),
  em: dataHoraSchema,
});
export type EventoAprovacao = z.infer<typeof eventoAprovacaoSchema>;

/** A resposta do `GET …/:id/aprovacao`: o estado e o ciclo inteiro, do mais antigo ao mais novo. */
export const aprovacaoDoDocumentoSchema = z.object({
  /** O parâmetro da conta para este documento, lido agora. */
  exigida: z.boolean(),
  situacao: situacaoAprovacaoSchema,
  eventos: z.array(eventoAprovacaoSchema),
});
export type AprovacaoDoDocumento = z.infer<typeof aprovacaoDoDocumentoSchema>;

/* ------------------------------ Regras puras ------------------------------ */

/**
 * O documento pode seguir para a próxima fase: o parâmetro está desligado, ou
 * ele foi aprovado. Usada pela API nas três travas e pela tela para desligar o
 * botão antes de o servidor recusar.
 */
export function liberadoPelaAprovacao(exigida: boolean, situacao: SituacaoAprovacao): boolean {
  return !exigida || situacao === 'APROVADA';
}

/**
 * A aprovação trava a edição: enviado, aprovado ou reprovado só se altera
 * depois de reaberto. É o que garante que o aprovado é exatamente o que segue
 * adiante — decisão do Adriano: o aprovado só muda reabrindo, e reabrir pede
 * aprovação nova.
 */
export function travadoPelaAprovacao(exigida: boolean, situacao: SituacaoAprovacao): boolean {
  return exigida && situacao !== 'NAO_REQUER';
}

/** Os gestos que cabem em cada situação, com o parâmetro ligado. */
export function gestosDaAprovacao(
  exigida: boolean,
  situacao: SituacaoAprovacao,
): { enviar: boolean; decidir: boolean; reabrir: boolean } {
  return {
    enviar: exigida && situacao === 'NAO_REQUER',
    decidir: exigida && situacao === 'AGUARDANDO',
    // Inclusive do aguardando: quem enviou e viu um erro antes da decisão
    // recolhe o documento, em vez de pedir ao autorizador que reprove.
    reabrir: exigida && situacao !== 'NAO_REQUER',
  };
}

/**
 * O documento ainda está em curso, e só nele os gestos da autorização cabem.
 *
 * A solicitação **em cotação** está em curso: a cotação é livre antes da
 * aprovação (decisão do Adriano), e é justamente nela que a decisão do
 * autorizador ainda falta para o pedido nascer. A lista gerada, o pedido
 * atendido e todo documento encerrado já não têm o que autorizar.
 */
export function documentoEmCursoParaAprovacao(
  documento: DocumentoAprovacao,
  situacao: string,
): boolean {
  if (documento === 'SOLICITACAO') return situacao === 'PENDENTE' || situacao === 'COTACAO';
  return situacao === 'PENDENTE';
}

/**
 * Quais documentos a conta autoriza — a resposta de
 * `GET /api/v1/compras/aprovacao/parametros`, que todo usuário autenticado lê.
 *
 * Existe à parte da configuração da conta porque aquela exige
 * `admin.conta.ver`, e quem lança pedido precisa saber se ele vai precisar de
 * aprovação: é o que decide se a tela mostra o botão de enviar e se a entrada
 * de nota esconde o pedido não aprovado.
 */
export const parametrosDeAprovacaoSchema = z.object({
  LISTA_COMPRA_DIRETA: z.boolean(),
  SOLICITACAO: z.boolean(),
  PEDIDO: z.boolean(),
});
export type ParametrosDeAprovacao = z.infer<typeof parametrosDeAprovacaoSchema>;

/* ------------------------- Fila do autorizador -------------------------- */

/**
 * Um documento na fila do autorizador (`CMP·08`, decisão 109): aguardando, de
 * um tipo cuja autorização a conta liga e que quem pergunta pode decidir.
 *
 * Os três documentos numa linha só, com o que serve para decidir sem abrir:
 * o número, a data, a empresa (a tela não a mostra desde 23/10/2026, mas o
 * contrato a mantém para quem integra), o detalhe — a obra da lista e da solicitação, o
 * fornecedor do pedido —, o valor (só o pedido tem) e quem enviou, quando.
 */
export const pendenciaDeAprovacaoSchema = z.object({
  documento: documentoAprovacaoSchema,
  id: uuidSchema,
  numero: z.number().int(),
  data: dataSchema,
  empresaNome: z.string().nullable(),
  /** A obra da lista e da solicitação; o fornecedor do pedido. */
  detalhe: z.string().nullable(),
  /**
   * O total do pedido, ou o total previsto da lista — nulo quando algum material
   * dela perdeu o acordo. A solicitação não tem preço, e vem sempre nula.
   */
  valor: dinheiroSchema.nullable(),
  itens: z.number().int(),
  enviadoPorNome: z.string().nullable(),
  enviadoEm: dataHoraSchema.nullable(),
  /**
   * Já foi reprovado ao menos uma vez neste ciclo de vida — e voltou corrigido
   * (decisão 111). A fila marca "Revisado": o autorizador sabe que está vendo
   * a segunda versão, e abre o histórico para conferir se o motivo foi atendido.
   */
  revisado: z.boolean(),
});
export type PendenciaDeAprovacao = z.infer<typeof pendenciaDeAprovacaoSchema>;

export const pendenciasDeAprovacaoSchema = z.object({
  data: z.array(pendenciaDeAprovacaoSchema),
});
export type PendenciasDeAprovacao = z.infer<typeof pendenciasDeAprovacaoSchema>;

// `.strict()`: parâmetro desconhecido é recusado, e não descartado em silêncio
// — ver `query-estrita.test.ts`.
export const listarPendenciasDeAprovacaoQuerySchema = z
  .object({ documento: documentoAprovacaoSchema.optional() })
  .strict();
export type ListarPendenciasDeAprovacaoQuery = z.infer<
  typeof listarPendenciasDeAprovacaoQuerySchema
>;

/** O teto do lote: uma tela cheia, com folga. */
export const MAXIMO_DECISOES_EM_LOTE = 100;

/**
 * A mesma decisão sobre vários documentos (`CMP·08`). Na reprovação, o mesmo
 * motivo vai para todos — quem reprova vinte pedidos de uma vez o faz por uma
 * razão só; motivos diferentes se dão um a um.
 */
export const decisaoEmLoteSchema = z
  .object({
    documentos: z
      .array(z.object({ documento: documentoAprovacaoSchema, id: uuidSchema }).strict())
      .min(1, 'Selecione ao menos um documento.')
      .max(MAXIMO_DECISOES_EM_LOTE, `No máximo ${String(MAXIMO_DECISOES_EM_LOTE)} por vez.`),
    decisao: decisaoAprovacaoSchema,
  })
  .strict();
export type DecisaoEmLote = z.infer<typeof decisaoEmLoteSchema>;

/**
 * O que aconteceu com cada documento do lote. **Cada um na sua transação**: o
 * que outro autorizador decidiu antes, ou que foi reaberto enquanto a tela
 * estava aberta, volta com o motivo, e os demais seguem decididos.
 */
export const resultadoDoLoteSchema = z.object({
  resultados: z.array(
    z.object({
      documento: documentoAprovacaoSchema,
      id: uuidSchema,
      ok: z.boolean(),
      /** Por que não foi decidido. Nulo no que deu certo. */
      mensagem: z.string().nullable(),
    }),
  ),
});
export type ResultadoDoLote = z.infer<typeof resultadoDoLoteSchema>;

/* --------------------------- Envio em lote ------------------------------ */

/**
 * Um documento ainda não enviado para aprovação (`CMP·09`, decisão 110): em
 * curso, de um tipo cuja autorização a conta liga e que quem pergunta pode
 * editar. Os mesmos campos da fila do autorizador, com quem **lançou** no lugar
 * de quem enviou — aqui ninguém enviou ainda.
 */
export const documentoAEnviarSchema = z.object({
  documento: documentoAprovacaoSchema,
  id: uuidSchema,
  numero: z.number().int(),
  data: dataSchema,
  /** A obra da lista e da solicitação; o fornecedor do pedido. */
  detalhe: z.string().nullable(),
  /** O total do pedido, ou o previsto da lista. A solicitação não tem preço. */
  valor: dinheiroSchema.nullable(),
  itens: z.number().int(),
  lancadoPorNome: z.string().nullable(),
  lancadoEm: dataHoraSchema,
});
export type DocumentoAEnviar = z.infer<typeof documentoAEnviarSchema>;

export const documentosAEnviarSchema = z.object({ data: z.array(documentoAEnviarSchema) });
export type DocumentosAEnviar = z.infer<typeof documentosAEnviarSchema>;

export const listarDocumentosAEnviarQuerySchema = z
  .object({ documento: documentoAprovacaoSchema.optional() })
  .strict();
export type ListarDocumentosAEnviarQuery = z.infer<typeof listarDocumentosAEnviarQuerySchema>;

/** Vários documentos para a fila do autorizador de uma vez. A resposta é a do lote. */
export const envioEmLoteSchema = z
  .object({
    documentos: z
      .array(z.object({ documento: documentoAprovacaoSchema, id: uuidSchema }).strict())
      .min(1, 'Selecione ao menos um documento.')
      .max(MAXIMO_DECISOES_EM_LOTE, `No máximo ${String(MAXIMO_DECISOES_EM_LOTE)} por vez.`),
  })
  .strict();
export type EnvioEmLote = z.infer<typeof envioEmLoteSchema>;
